import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { newsAPI, libraryAPI } from '../../services/api';
import './AdminPanel.scss';

// Components
import AdminStats from './components/AdminStats/AdminStats';
import NewsManager from './components/NewsManager/NewsManager';
import LibraryManager from './components/LibraryManager/LibraryManager';
import CategoryManager from './components/CategoryManager/CategoryManager';
import AuthorManager from './components/AuthorManager/AuthorManager';

// Modals
import CategoryModal from './components/CategoryManager/CategoryModal';
import AuthorModal from './components/AuthorManager/AuthorModal';

const AdminPanel = () => {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('news');
  const [message, setMessage] = useState(null);
  const [stats, setStats] = useState({ news: 0, books: 0, authors: 0 });
  
  // Shared Modals State (invoked from Library/News managers or direct access)
  const [activeModal, setActiveModal] = useState(null); // 'newsCategory', 'bookCategory', 'author', null

  useEffect(() => {
    if (isAdmin) {
      loadStats();
    }
  }, [isAdmin, activeTab]);

  const loadStats = async () => {
    try {
      // Parallel loading for stats
      const [newsRes, booksRes, authorsRes] = await Promise.all([
        newsAPI.getAll({ limit: 1 }), // Just to get count if API supports it or we fetch all
        libraryAPI.getBooks({ limit: 1 }),
        libraryAPI.getAuthors({ limit: 1 })
      ]);
      
      // Assuming API returns count or we use length of results (inefficient for large data but okay for now)
      // Better API would have /stats endpoint
      const newsCount = newsRes.data?.count || newsRes.data?.length || (Array.isArray(newsRes.data) ? newsRes.data.length : 0);
      const booksCount = booksRes.data?.count || booksRes.data?.length || (Array.isArray(booksRes.data) ? booksRes.data.length : 0);
      const authorsCount = authorsRes.data?.count || authorsRes.data?.length || (Array.isArray(authorsRes.data) ? authorsRes.data.length : 0);

      setStats({ news: newsCount, books: booksCount, authors: authorsCount });
    } catch (error) {
      console.error('Error loading stats', error);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  // Handlers for shared modals
  const handleCategoryCreate = async (formData) => {
    try {
      if (activeModal === 'newsCategory') {
        await newsAPI.createCategory(formData);
      } else if (activeModal === 'bookCategory') {
        await libraryAPI.createCategory(formData);
      }
      showMessage('Категория успешно создана!');
      setActiveModal(null);
      // Trigger reload in managers? Ideally use context or lift state up fully, 
      // but for now managers reload their own data on mount/updates. 
      // If we are in LibraryManager, it might need to reload categories.
      // We can pass a "refreshTrigger" prop to managers.
    } catch (error) {
      console.error('Error creating category', error);
      showMessage('Ошибка при создании категории', 'error');
    }
  };

  const handleAuthorCreate = async (formData) => {
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) data.append(key, formData[key]);
      });
      await libraryAPI.createAuthor(data);
      showMessage('Автор успешно добавлен!');
      setActiveModal(null);
    } catch (error) {
      console.error('Error adding author', error);
      showMessage('Ошибка при добавлении автора', 'error');
    }
  };

  if (!isAdmin) {
    return (
      <div className="admin-panel">
        <div className="container">
          <div className="access-denied">
            <h2>⛔ Доступ запрещен</h2>
            <p>У вас нет прав для просмотра этой страницы</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="container">
        <div className="admin-header">
          <h1 className="panel-title">⚙️ Панель администратора</h1>
          <AdminStats 
            newsCount={stats.news} 
            booksCount={stats.books} 
            authorsCount={stats.authors} 
          />
        </div>

        <div className="admin-tabs">
          <button
            className={`tab-button ${activeTab === 'news' ? 'active' : ''}`}
            onClick={() => setActiveTab('news')}
          >
            📰 Новости
          </button>
          <button
            className={`tab-button ${activeTab === 'library' ? 'active' : ''}`}
            onClick={() => setActiveTab('library')}
          >
            📚 Библиотека
          </button>
          <button
            className={`tab-button ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            🏷️ Категории
          </button>
          <button
            className={`tab-button ${activeTab === 'authors' ? 'active' : ''}`}
            onClick={() => setActiveTab('authors')}
          >
            ✍️ Авторы
          </button>
        </div>

        {message && (
          <div className={`message ${message.type === 'error' ? 'error' : 'success'}`}>
            {message.text}
          </div>
        )}

        <div className="tab-content">
          {activeTab === 'news' && (
            <NewsManager showMessage={showMessage} />
          )}
          
          {activeTab === 'library' && (
            <LibraryManager 
              showMessage={showMessage} 
              openAuthorModal={() => setActiveModal('author')}
              openCategoryModal={() => setActiveModal('bookCategory')}
            />
          )}
          
          {activeTab === 'categories' && (
            <CategoryManager showMessage={showMessage} />
          )}
          
          {activeTab === 'authors' && (
            <AuthorManager showMessage={showMessage} />
          )}
        </div>
      </div>

      {/* Shared Modals */}
      {(activeModal === 'newsCategory' || activeModal === 'bookCategory') && (
        <CategoryModal
          type={activeModal}
          isOpen={true}
          onClose={() => setActiveModal(null)}
          onSubmit={handleCategoryCreate}
          loading={false}
        />
      )}

      {activeModal === 'author' && (
        <AuthorModal
          isOpen={true}
          onClose={() => setActiveModal(null)}
          onSubmit={handleAuthorCreate}
          loading={false}
        />
      )}
    </div>
  );
};

export default AdminPanel;
